import { useTranslations } from 'next-intl';
import { ReportsSearchFilterOptionType, ReportsSearchFilterType } from './SearchFilters';
import DropdownFilter, { DropdownFilterOptionType, IDropdownFilter } from './DropdownFilter';
import { useEffect, useState } from 'react';
import { assignValuesFromRouteQuery, pushValuesToRouteQuery } from '@/utils';
import { useRouter } from 'next/router';
import { ReportsSearchFacetsType } from '@/types';
import classNames from 'classnames';

type FilterValuesType = {
  [key: string]: string[];
};

type SearchFilterOptionsType = {
  availableFilters: ReportsSearchFilterType[];
  facets?: ReportsSearchFacetsType[];
  isPublic: boolean;
  className?: string;
  clearBtnLabel?: string;
  value?: FilterValuesType;
  onChange?: (value: FilterValuesType) => void;
};

function extractYear(label: string): number {
  const match = label.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

const SearchFilterOptions = (data: SearchFilterOptionsType) => {
  const { availableFilters, facets, isPublic, value, className, clearBtnLabel, onChange: onValueChange } = data;
  const t = useTranslations();
  const router = useRouter();

  const isControlled = value !== undefined;

  const [filters, setFilters] = useState<IDropdownFilter[]>([]);
  const [changeFilters, setChangeFilters] = useState<FilterValuesType>();

  useEffect(() => {
    const filterFields: FilterValuesType = {};

    availableFilters.forEach((filter) => {
      filterFields[filter.key] = value?.[filter.key] ?? [];
    });

    if (!isControlled) {
      assignValuesFromRouteQuery(router, filterFields);
    }

    setChangeFilters(filterFields);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFilters, facets, value, isControlled]);

  useEffect(() => {
    if (!changeFilters) return;

    buildFilters();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeFilters, availableFilters, facets]);

  useEffect(() => {
    if (isControlled || !changeFilters) return;

    const filterFields = { ...changeFilters };
    assignValuesFromRouteQuery(router, filterFields);
    setChangeFilters(filterFields);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query, isControlled]);

  const buildFilters = () => {
    const filters: IDropdownFilter[] = [];
    let didUnselect = false;

    const mapOption = (
      option: ReportsSearchFilterOptionType,
      facet: ReportsSearchFacetsType | null | undefined,
    ): DropdownFilterOptionType => {
      const optionOb: DropdownFilterOptionType = {
        label: option.name,
        value: option.id,
        count: 0,
        disabled: false,
      };

      // note: we don't have facets on stat bank search for reasons...
      if (facet) {
        const facetValue = facet.values.find((facetValue) => facetValue.value && facetValue.value.id === option.id);

        optionOb.label = `${optionOb.label} (${facetValue ? facetValue.count : 0})`;
        optionOb.count = facetValue ? facetValue.count : 0;
        optionOb.disabled = !facetValue;
      }

      if (option.options && option.options.length > 0) {
        optionOb.options = option.options.map((child) => mapOption(child, facet));
      }

      return optionOb;
    };

    availableFilters.forEach((filter) => {
      const facet = facets ? facets.find((facet) => facet.name === filter.name) : null;

      let disabled = true;

      const filterOb: IDropdownFilter = {
        name: filter.key,
        disabled: false,
        value: [],
        options: filter.options.map((option) => {
          const optionOb = mapOption(option, facet);

          if (facet) {
            if (!optionOb.disabled) disabled = false;

            // also check nested children
            if (optionOb.options && optionOb.options.some((o) => !o.disabled)) {
              disabled = false;
            }
          } else {
            disabled = false;
          }

          return optionOb;
        }),
      };

      if (filter.key === 'years') {
        filterOb.options = filterOb.options
          .filter((o) => o.value !== 'Forecast')
          .sort((a, b) => parseInt(b.value) - parseInt(a.value));
      }

      if (filter.key === 'seasons') {
        filterOb.options = filterOb.options.sort((a, b) => extractYear(b.label) - extractYear(a.label));
      }

      if ('brand' == filter.key) {
        filterOb.options = filterOb.options.sort((a, b) => a.label.localeCompare(b.label));
      }

      if (facet && changeFilters) {
        const changeFilterArray = changeFilters[filter.key];

        if (changeFilterArray && changeFilterArray.length > 0) {
          const didUnselect0 = removeFromChangeFiltersWhereNoResults(facet, changeFilterArray);
          didUnselect = didUnselect || didUnselect0;
        }

        if (shouldRemoveZeroResultOptionsFromFilter(filter.key)) {
          filterOb.options = filterOb.options.filter((o) => o.count > 0);
        }
      }

      filterOb.disabled = disabled;

      filters.push(filterOb);
    });

    // this is more correct, but looks crapper
    // if (didUnselect)
    //   this.pushFiltersToRoute();

    setFilters(filters);
  };

  // loop through each id in changeFilterArray and check that it is in the facet result so we don't select options that have no results
  // that is we don't want a drop down option checked when it's count is zero, as it's disabled then and you can't unselect it.
  const removeFromChangeFiltersWhereNoResults = (facet: ReportsSearchFacetsType, changeFilterArray: string[]) => {
    let didUnselect = false;

    for (let i = changeFilterArray.length - 1; i >= 0; --i) {
      const id = changeFilterArray[i];
      const facetInfo = facet.values.find((fr) => fr.value?.id === id);

      if (!facetInfo) {
        changeFilterArray.splice(i, 1);
        didUnselect = true;
      }
    }

    return didUnselect;
  };

  const shouldRemoveZeroResultOptionsFromFilter = (filterKeyName: string) => {
    if (isPublic) return true;

    switch (filterKeyName) {
      case 'formats':
      case 'report-types':
        return true;
      default:
        return false;
    }
  };

  const findOptionByValue = (options: DropdownFilterOptionType[], value: string): DropdownFilterOptionType | null => {
    for (const option of options) {
      if (option.value === value) return option;

      if (option.options && option.options.length > 0) {
        const childMatch = findOptionByValue(option.options, value);
        if (childMatch) return childMatch;
      }
    }

    return null;
  };

  const getDescendantValues = (option: DropdownFilterOptionType): string[] => {
    if (!option.options || option.options.length === 0) return [];

    return option.options.flatMap((child) => [child.value, ...getDescendantValues(child)]);
  };

  const updateFilterValues = (newFilterValues: FilterValuesType) => {
    if (isControlled) {
      onValueChange?.(newFilterValues);
    } else {
      pushValuesToRouteQuery(router, { ...newFilterValues, page: null });
    }
  };

  const onFilterChange = (filterName: string, selectedValue: string) => {
    if (!changeFilters) return;

    const newFilterValues: FilterValuesType = { ...changeFilters };

    const targetFilter = filters.find((filter) => filter.name === filterName);
    const selectedOption = targetFilter ? findOptionByValue(targetFilter.options, selectedValue) : null;
    const descendants = selectedOption ? getDescendantValues(selectedOption) : [];

    if (newFilterValues[filterName].includes(selectedValue)) {
      const valuesToRemove = new Set([selectedValue, ...descendants]);
      newFilterValues[filterName] = newFilterValues[filterName].filter((v) => !valuesToRemove.has(v));
    } else {
      const valuesToAdd = [selectedValue, ...descendants];
      newFilterValues[filterName] = Array.from(new Set([...newFilterValues[filterName], ...valuesToAdd]));
    }

    setChangeFilters(newFilterValues);
    updateFilterValues(newFilterValues);
  };

  const onClear = (filterName: string) => {
    if (!changeFilters) return;

    const newFilterValues: FilterValuesType = {
      ...changeFilters,
      [filterName]: [],
    };

    setChangeFilters(newFilterValues);
    updateFilterValues(newFilterValues);
  };

  return (
    <div className={classNames('flex w-full flex-col flex-wrap items-center gap-2 md:flex-row lg:gap-4', className)}>
      {changeFilters &&
        filters.map((filter) => (
          <DropdownFilter
            key={filter.name}
            value={changeFilters[filter.name]}
            name={t('search.$filterlabel' + filter.name.replace(/[\W_]+/g, ''))}
            options={filter.options}
            disabled={filter.disabled}
            onChange={(value) => onFilterChange(filter.name, value)}
            onClear={() => onClear(filter.name)}
            clearBtnLabel={clearBtnLabel}
          />
        ))}
    </div>
  );
};

export default SearchFilterOptions;
